import React, { useEffect, useRef, useState, useCallback } from 'react'
import Wrapper from '../../components/Wrapper'
import { Helmet } from 'react-helmet'
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  InputAdornment,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import RefreshIcon from '@mui/icons-material/Refresh'
import { chat, menu } from '../../translate'
import Api from '../../Api'
import { chatStyles as styles, greenTheme } from '../../helpers/style'
import { useParams, useLocation } from 'react-router-dom'

function TelegramMessagePhoto({ accountId, chatId, messageId, sx = {} }) {
  const [src, setSrc] = useState(null)
  const [err, setErr] = useState(false)
  const blobUrlRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setErr(false)
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    setSrc(null)
    if (!accountId || !chatId || !messageId) return

    Api.telegramFileGet(accountId, chatId, messageId)
      .then((res) => {
        if (cancelled) return
        const blob = res.data
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob)
          blobUrlRef.current = url
          setSrc(url)
        }
      })
      .catch(() => {
        if (!cancelled) setErr(true)
      })

    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [accountId, chatId, messageId])

  if (err) return <Typography variant="caption" color="text.secondary">[Photo]</Typography>
  if (!src) return <Typography variant="caption" color="text.secondary">…</Typography>
  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        maxWidth: '100%',
        maxHeight: 240,
        borderRadius: 1,
        display: 'block',
        mt: 0.5,
        ...sx,
      }}
    />
  )
}

export default function Chats({ answerer: answererProp }) {
  const { accountId } = useParams()
  const location = useLocation()
  const isHistoryTelegram = location.pathname.includes('/history_telegram') && !answererProp
  const answerer = answererProp ?? ''
  const [chats, setChats] = useState([])
  const [selectedChat, setSelectedChat] = useState(null)

  const [messages, setMessages] = useState([])
  const [message, setMessage] = useState('')

  const [loading, setLoading] = useState(false)
  const [loadingChats, setLoadingChats] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const threadRef = useRef(null)
  const langIndex = +localStorage.getItem('atsLang') || 1
  const pollRef = useRef(null)
  const lastMessageIdRef = useRef(null)

  const transformTelegramChat = (chatItem) => {
    const lastMessageDate = chatItem.last_message?.date
      ? new Date(chatItem.last_message.date * 1000).toISOString()
      : null

    return {
      id: chatItem.id,
      name: chatItem.title || `Chat ${chatItem.id}`,
      phone: chatItem.id,
      lastMessage: '',
      createdAt: lastMessageDate || new Date().toISOString(),
      unread: (chatItem.unread_count || 0) > 0,
      unread_count: chatItem.unread_count || 0,
      type: chatItem.type,
      is_pinned: chatItem.is_pinned || false,
      _telegram: chatItem,
    }
  }

  const loadTelegramChats = useCallback(
    async (refresh = 0) => {
      try {
        if (refresh) setRefreshing(true)
        else setLoadingChats(true)

        const res = await Api.telegramChatsGet(accountId, refresh)
        const telegramChats = res.data?.chats || []
        const transformedChats = telegramChats.map(transformTelegramChat)

        setChats(transformedChats)
      } catch (error) {
        console.error('Error loading Telegram chats:', error)
        setChats([])
      } finally {
        setRefreshing(false)
        setLoadingChats(false)
      }
    },
    [accountId]
  )

  useEffect(() => {
    loadTelegramChats()
  }, [loadTelegramChats])

  const handleRefresh = useCallback(() => {
    loadTelegramChats(1)
  }, [loadTelegramChats])

  const mapDbMessageToUi = (m) => {
    const iso = m.date ? new Date(Number(m.date) * 1000).toISOString() : new Date().toISOString()
    const from = m.is_out ? 'user' : 'admin'
    const hasFile = m.content_type === 'photo' || (m.file && (m.file.local_path || m.file.file_id))
    return {
      id: m.message_id,
      from,
      text: m.text || '',
      createdAt: iso,
      answerer: m.answerer || null,
      content_type: m.content_type || null,
      hasFile: !!hasFile,
      _raw: m,
    }
  }

  const loadMessages = useCallback(async () => {
    if (!selectedChat) return

    setLoading(true)
    try {
      const res = await Api.telegramMessagesGet(accountId, selectedChat.id, { limit: 50 })
      const rows = res.data?.messages || []
      const ui = rows.map(mapDbMessageToUi)
      setMessages(ui)

      const last = rows.length ? rows[rows.length - 1] : null
      lastMessageIdRef.current = last?.message_id ? String(last.message_id) : null
      await Api.telegramChatRead(accountId, selectedChat.id)

    } catch (error) {
      console.error('Error loading messages:', error)
      setMessages([])
      lastMessageIdRef.current = null
    } finally {
      setLoading(false)
    }
  }, [selectedChat, accountId])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const pollNewMessages = useCallback(async () => {
    if (!selectedChat) return
    const after = lastMessageIdRef.current
    if (!after) return

    try {
      const res = await Api.telegramMessagesGet(accountId, selectedChat.id, {
        afterMessageId: after,
        limit: 200,
      })
      const rows = res.data?.messages || []
      if (!rows.length) return

      setMessages((prev) => {
        const existing = new Set(prev.map((x) => String(x.id)))
        const add = rows
          .filter((r) => !existing.has(String(r.message_id)))
          .map(mapDbMessageToUi)
        return add.length ? [...prev, ...add] : prev
      })

      const last = rows[rows.length - 1]
      if (last?.message_id) lastMessageIdRef.current = String(last.message_id)

      loadTelegramChats(0)

    } catch (e) {}
  }, [selectedChat, accountId])

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
    if (!selectedChat) return
    pollRef.current = setInterval(() => {
      pollNewMessages()
    }, 4000)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [selectedChat, pollNewMessages])

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [messages])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!message.trim() || !selectedChat) return

    const textToSend = message.trim()
    setMessage('')
    const optimisticId = `tmp_${Date.now()}`
    const nowIso = new Date().toISOString()
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        from: 'user',
        text: textToSend,
        createdAt: nowIso,
        _optimistic: true,
      },
    ])

    try {
      const r = await Api.telegramMessagesSend(accountId, selectedChat.id, textToSend, answerer)
      if (r?.data?.messageId) {
        lastMessageIdRef.current = String(r.data.messageId)
      }
      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticId ? { ...m, id: r?.data?.messageId || m.id, _optimistic: false } : m))
      )
    } catch (e2) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          from: 'system',
          text: chat.error?.[langIndex] || 'Error sending message.',
          createdAt: new Date().toISOString(),
        },
      ])
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const formatTimestamp = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  }

  const formatDate = (value) => {
    if (!value) return ''
    const timestamp = typeof value === 'number' ? value * 1000 : value
    const d = new Date(timestamp)
    if (Number.isNaN(d.getTime())) return ''
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (d.toDateString() === today.toDateString()) return formatTimestamp(timestamp)
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString()
  }

  const getDayKey = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toISOString().slice(0, 10)
  }

  const formatDayLabel = (value) => {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString()
  }

  const getLastMessagePreview = (chatItem) => {
    if (chatItem.lastMessage) {
      return chatItem.lastMessage.length > 50 ? chatItem.lastMessage.substring(0, 50) + '...' : chatItem.lastMessage
    }
    if (chatItem._telegram?.last_message) return chat.start?.[langIndex] || 'New message'
    return chat.start?.[langIndex] || 'Start conversation'
  }

  const content = (
    <div className="tableParent" style={{marginTop: '10px'}}>
      <Box sx={styles.container}>
            <Paper
              sx={{
                ...styles.sidebar,
                display: {
                  xs: selectedChat ? 'none' : 'flex',
                  md: sidebarOpen ? 'flex' : 'none',
                },
                zIndex: { xs: sidebarOpen ? 1300 : 1, md: selectedChat ? 1 : 1300 },
              }}
            >
              <Box sx={styles.searchBar}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={chat.message?.[langIndex] || 'Search chats...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={styles.searchIcon} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleRefresh}
                          disabled={refreshing}
                          sx={{
                            color: '#9e9e9e',
                            '&:hover': { backgroundColor: 'rgba(126, 188, 10, 0.1)', color: '#7ebc0a' },
                            '&.Mui-disabled': { color: '#9e9e9e' },
                            ...(refreshing && {
                              animation: 'spin 1s linear infinite',
                              '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' },
                              },
                            }),
                          }}
                          aria-label="Refresh chats"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={styles.searchInput}
                />
              </Box>

              <Box sx={styles.chatList}>
                {loadingChats ? (
                  <Box sx={styles.loadingBox}>
                    <Typography variant="body2" color="text.secondary">
                      Loading chats...
                    </Typography>
                  </Box>
                ) : chats.length === 0 ? (
                  <Box sx={styles.loadingBox}>
                    <Typography variant="body2" color="text.secondary">
                      No chats found
                    </Typography>
                  </Box>
                ) : (
                  <List sx={styles.chatListContainer}>
                    {chats.map((chatItem, index) => (
                      <React.Fragment key={chatItem.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={selectedChat?.id === chatItem.id}
                            onClick={() => setSelectedChat(chatItem)}
                            sx={styles.chatItem}
                          >
                            <ListItemAvatar sx={styles.listItemAvatar}>
                              <Avatar sx={styles.avatar(selectedChat?.id === chatItem.id)}>
                                {chatItem.name?.[0]?.toUpperCase() || 'C'}
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={styles.chatName(selectedChat?.id === chatItem.id)}>
                                  {chatItem.name || 'Support'}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={styles.secondaryText}>
                                  {getLastMessagePreview(chatItem)}
                                </Typography>
                              }
                            />

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Typography variant="caption" sx={styles.timestamp}>
                                {formatDate(chatItem.createdAt)}
                              </Typography>
                              {chatItem.unread_count > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    backgroundColor: '#0088cc',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    minWidth: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 6px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {chatItem.unread_count}
                                </Typography>
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                        {index < chats.length - 1 && <Divider sx={styles.divider} />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Paper>

            <Paper
              sx={{
                ...styles.conversationPaper,
                display: { xs: selectedChat ? 'flex' : 'none', md: 'flex' },
              }}
            >
              {selectedChat ? (
                <>
                  <Box sx={styles.chatHeader}>
                    <IconButton
                      onClick={() => {
                        if (window.innerWidth < 960) {
                          setSelectedChat(null)
                        } else {
                          setSidebarOpen(!sidebarOpen)
                        }
                      }}
                      sx={styles.menuButton}
                      aria-label="Toggle chat list"
                    >
                      <MenuIcon />
                    </IconButton>
                    <Avatar sx={styles.headerAvatar}>{selectedChat.name?.[0]?.toUpperCase() || 'C'}</Avatar>
                    <Box sx={styles.headerInfoBox}>
                      <Typography variant="h6" sx={styles.headerName}>
                        {selectedChat.name || 'Support'}
                      </Typography>
                      {selectedChat.phone && (
                        <Typography variant="caption" sx={styles.headerPhone}>
                          {selectedChat.phone}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box ref={threadRef} sx={styles.messagesThread}>
                    {loading ? (
                      <Box sx={styles.loadingMessagesBox}>
                        <Typography variant="body2" color="text.secondary">
                          Loading messages...
                        </Typography>
                      </Box>
                    ) : messages.length === 0 ? (
                      <Box sx={styles.emptyMessagesBox}>
                        <Typography variant="body1" color="text.secondary" sx={styles.emptyStateSubtitle}>
                          {chat.start?.[langIndex] || 'Start a conversation'}
                        </Typography>
                      </Box>
                    ) : (
                      (() => {
                        let lastDay = ''
                        return messages.map((msg) => {
                          const dayKey = getDayKey(msg.createdAt)
                          const showDay = dayKey && dayKey !== lastDay
                          if (dayKey) lastDay = dayKey

                          const isUser = msg.from === 'user'
                          const isSystem = msg.from === 'system'

                          return (
                            <React.Fragment key={msg.id}>
                              {showDay && (
                                <Box sx={styles.daySeparatorBox}>
                                  <Typography variant="caption" sx={styles.dayLabel}>
                                    {formatDayLabel(msg.createdAt)}
                                  </Typography>
                                </Box>
                              )}

                              <Box
                                sx={{
                                  ...styles.messageContainer,
                                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                                }}
                              >
                                <Box sx={styles.messageBubble(isUser, isSystem)}>
                                  {msg.answerer && (
                                    <Typography variant="caption" sx={styles.answererLabel(isUser)}>
                                      {msg.answerer}
                                    </Typography>
                                  )}
                                  {msg.hasFile && (
                                    <TelegramMessagePhoto
                                      accountId={accountId}
                                      chatId={selectedChat.id}
                                      messageId={msg.id}
                                    />
                                  )}
                                  {msg.text ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ ...styles.messageText, color: isUser ? '#000000' : 'inherit' }}
                                    >
                                      {msg.text}
                                    </Typography>
                                  ) : null}

                                  <Typography
                                    variant="caption"
                                    sx={{ ...styles.messageTimestamp, opacity: isUser ? 0.8 : 0.6 }}
                                  >
                                    {formatTimestamp(msg.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>
                            </React.Fragment>
                          )
                        })
                      })()
                    )}
                  </Box>

                  <Box component="form" onSubmit={handleSubmit} sx={styles.inputArea}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={chat.message?.[langIndex] || 'Type your message...'}
                      variant="outlined"
                      size="small"
                      sx={styles.messageInput}
                      disabled={isHistoryTelegram}
                    />
                    <IconButton type="submit" color="primary" sx={styles.sendButton(message.trim())} disabled={!message.trim()}>
                      <SendIcon />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Box sx={styles.emptyState}>
                  {window.innerWidth >= 960 && !sidebarOpen && (
                    <IconButton onClick={() => setSidebarOpen(true)} sx={styles.emptyStateButton}>
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Box sx={styles.emptyStateText}>
                    <Typography variant="h6" color="text.secondary" sx={styles.emptyStateTitle}>
                      Select a chat to start messaging
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={styles.emptyStateSubtitle}>
                      {window.innerWidth >= 960 && !sidebarOpen
                        ? 'Click the menu button to view your chats'
                        : 'Choose a conversation from the list to begin'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </div>
  )

  return (
    <>
      <Helmet>
        <title>{'Chats'}</title>
      </Helmet>

      {location.pathname.includes('/history_telegram') ? (
        content
      ) : (
        <Wrapper>
          {content}
        </Wrapper>
      )}
    </>
  )
}
